﻿import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Box,
  Typography,
  Autocomplete,
  TextField,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { ShepButton } from "@/components/GenericComponents/ShepComponents";
import axios from "@/features/axiosHelper";
import { useDispatch } from "react-redux";
import { createAlert } from "@/features/homepage/alertSliceList";
import { useWorkflowLookups } from "../WorkflowLookupsContext";
import { CatalogTypes } from "@/components/Maintenance/MaintenanceTypes";
import { WorkflowLookupMode } from "@/components/Maintenance/MaintenanceTypes";

const labelSx = { mr: 3, minWidth: 265, alignItems: "right" };

const AddDefaultWarehousePopup = ({
  open,
  onClose,
  workflowLookupType,
  fetchData,
  mode = WorkflowLookupMode.Add,
  existingRow = null,
}) => {
  const dispatch = useDispatch();

  // Use context for customer and ERP connections and loading
  const {
    fetchCatalogArtifactData,
    customerConnections,
    customersLoaded,
    ERPConnections,
    rows,
  } = useWorkflowLookups();

  // Autocomplete selections
  const [selectedCustomerConnection, setSelectedCustomerConnection] =
    useState(null);
  const [selectedERPConnection, setSelectedERPConnection] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [shipTos, setShipTos] = useState([]);
  const [items, setItems] = useState([]);
  const [warehouse, setWarehouse] = useState([]);
  const [selectedERPRelatedCustomer, setSelectedERPRelatedCustomer] =
    useState(null);
  const [selectedERPShipTo, setSelectedERPShipTo] = useState(null);
  const [selectedERPItem, setSelectedERPItem] = useState(null);
  const [selectedERPWarehouse, setSelectedERPWarehouse] = useState(null);
  // loading states
  const [customersLoading, setCustomersLoading] = useState(false);
  const [shipTosLoading, setShipTosLoading] = useState(false);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [warehouseLoading, setWarehouseLoading] = useState(false);

  // Prefill on edit
  useEffect(() => {
    if (!open) return;

    if (mode === WorkflowLookupMode.Edit && existingRow) {
      const conn =
        customerConnections.find(
          (c) => c.ConnectionId === existingRow.CustomerConnectionId
        ) || null;
      setSelectedCustomerConnection(conn);

      // Existing record may or may not have ERPConnectionId/Name in JSON
      const erpConnFromRow =
        ERPConnections?.find(
          (e) => e.ConnectionId === existingRow.ERPConnectionId
        ) ||
        ERPConnections?.find(
          (e) =>
            existingRow.ERPConnectionName &&
            e.Name === existingRow.ERPConnectionName
        ) ||
        null;

      if (erpConnFromRow) {
        setSelectedERPConnection(erpConnFromRow);
      } else if (ERPConnections?.length === 1) {
        // Fallback for legacy records without ERP connection data
        setSelectedERPConnection(ERPConnections[0]);
      } else {
        setSelectedERPConnection(null);
      }
    } else if (mode === WorkflowLookupMode.Add) {
      setSelectedCustomerConnection(null);
      setSelectedERPConnection(null);
      setSelectedERPRelatedCustomer(null);
      setSelectedERPShipTo(null);
      setSelectedERPItem(null);
      setSelectedERPWarehouse(null);
      setCustomers([]);
      setShipTos([]);
      setItems([]);
      setWarehouse([]);
      setCustomersLoading(false);
      setShipTosLoading(false);
      setItemsLoading(false);
      setWarehouseLoading(false);
    }
  }, [open]);

  // Fetch catalog data when ERP connection changes
  useEffect(() => {
    if (selectedERPConnection?.ConnectionId != null) {
      setCustomersLoading(true);
      setShipTosLoading(true);
      setItemsLoading(true);
      setWarehouseLoading(true);

      Promise.all([
        fetchCatalogArtifactData({
          connectionId: selectedERPConnection.ConnectionId,
          catalogType: CatalogTypes.Customer,
        }),
        fetchCatalogArtifactData({
          connectionId: selectedERPConnection.ConnectionId,
          catalogType: CatalogTypes.ShipTo,
        }),
        fetchCatalogArtifactData({
          connectionId: selectedERPConnection.ConnectionId,
          catalogType: CatalogTypes.Item,
        }),
        fetchCatalogArtifactData({
          connectionId: selectedERPConnection.ConnectionId,
          catalogType: CatalogTypes.Warehouse,
        }),
      ])
        .then(([customerData, shipTsData, itemData, warehouseData]) => {
          setCustomers(customerData);
          setShipTos(shipTsData);
          setItems(itemData);
          setWarehouse(warehouseData);
        })
        .finally(() => {
          setCustomersLoading(false);
          setShipTosLoading(false);
          setItemsLoading(false);
          setWarehouseLoading(false);
        });
    } else {
      setCustomers([]);
      setShipTos([]);
      setItems([]);
      setWarehouse([]);
      setCustomersLoading(false);
      setShipTosLoading(false);
      setItemsLoading(false);
      setWarehouseLoading(false);
    }
  }, [selectedERPConnection, fetchCatalogArtifactData]);

  // After catalogs load, pre-select values based on existingRow in edit mode
  useEffect(() => {
    if (mode !== WorkflowLookupMode.Edit || !existingRow) return;
    if (
      !customers?.length &&
      !shipTos?.length &&
      !items?.length &&
      !warehouse?.length
    )
      return;

    if (customers?.length) {
      const cust = customers.find(
        (c) => c.CustomerCode === existingRow.ERPRelatedCustomer
      );
      setSelectedERPRelatedCustomer(cust || null);
      // If ShipTo doesn't match the selected customer after set, it gets cleared by onChange
    }
    if (shipTos?.length) {
      const st = shipTos.find(
        (s) => s.ShipToCode === existingRow.ERPShipToCode
      );
      setSelectedERPShipTo(st || null);
    }
    if (items?.length) {
      const it = items.find((i) => i.ItemCode === existingRow.ERPItemCode);
      setSelectedERPItem(it || null);
    }
    if (warehouse?.length) {
      const wh = warehouse.find((w) => w.Code === existingRow.ERPWarehouseCode);
      setSelectedERPWarehouse(wh || null);
    }
  }, [mode, existingRow, customers, shipTos, items, warehouse]);

  const validateForm = () => {
    if (!selectedCustomerConnection) {
      return {
        isValid: false,
        message: "Please select a customer connection before saving.",
      };
    }
    if (!selectedERPWarehouse?.Code) {
      return {
        isValid: false,
        message: "Please select a warehouse code before saving.",
      };
    }
    return { isValid: true };
  };

  const handleSubmit = async () => {
    const validation = validateForm();
    if (!validation.isValid) {
      dispatch(
        createAlert({
          status: "Error",
          message: validation.message,
        })
      );
      return;
    }

    const normalize = (v) =>
      v === null || v === undefined || v === 0 ? "" : String(v);

    const matchingExistingRow = rows.find((x) => {
      const erpExisting = normalize(x.ERPConnectionId);
      const erpSelected = normalize(selectedERPConnection?.ConnectionId);
      const erpMatches =
        erpExisting === "" || // legacy record without ERPConnectionId have same risk of conflict as before
        erpSelected === "" ||
        erpExisting === erpSelected;
      return (
        x.WorkflowLookupType === workflowLookupType &&
        normalize(x.CustomerConnectionId) ===
          normalize(selectedCustomerConnection?.ConnectionId) &&
        erpMatches &&
        normalize(x.ERPRelatedCustomer) ===
          normalize(selectedERPRelatedCustomer?.CustomerCode) &&
        normalize(x.ERPShipToCode) ===
          normalize(selectedERPShipTo?.ShipToCode) &&
        normalize(x.ERPItemCode) === normalize(selectedERPItem?.ItemCode)
      );
    });

    const payload = {
      workflowLookupType,
      CustomerConnectionId: selectedCustomerConnection?.ConnectionId ?? null,
      CustomerConnectionName: selectedCustomerConnection?.Name ?? "",
      ERPConnectionId: selectedERPConnection?.ConnectionId ?? null,
      ERPConnectionName: selectedERPConnection?.Name ?? "",
      ERPRelatedCustomer: selectedERPRelatedCustomer?.CustomerCode ?? "",
      ERPShipToCode: selectedERPShipTo?.ShipToCode ?? "",
      ERPItemCode: selectedERPItem?.ItemCode ?? "",
      ERPWarehouseCode: selectedERPWarehouse?.Code ?? "",
    };

    try {
      // Edit mode, update existing record directly
      if (mode === WorkflowLookupMode.Edit && existingRow?.WorkflowLookupId) {
        await axios.put(
          `/workflowlookups/${existingRow.WorkflowLookupId}`,
          payload
        );
      } else {
        if (matchingExistingRow && matchingExistingRow.WorkflowLookupId) {
          const userConfirmed = await new Promise((resolve) => {
            dispatch(
              createAlert({
                status: "Warning",
                alertType: "result",
                title: "Update Overlapping Record",
                message:
                  "A Default Warehouse for this connection already exists. Do you want to update it?",
                buttonLabel: "Update",
                onButtonClick: () => resolve(true),
                buttonLabel2: "Cancel",
                onButtonClick2: () => resolve(false),
              })
            );
          });

          if (!userConfirmed) {
            return;
          }

          await axios.put(
            `/workflowlookups/${matchingExistingRow.WorkflowLookupId}`,
            payload
          );
        } else {
          await axios.post("/workflowlookups", payload);
        }
      }

      if (fetchData) fetchData();
      handleClose();
    } catch (e) {
      dispatch(
        createAlert({
          status: "Error",
          message: e.message,
        })
      );
    }
  };

  const handleClose = () => {
    setSelectedCustomerConnection(null);
    setSelectedERPConnection(null);
    setSelectedERPRelatedCustomer(null);
    setSelectedERPShipTo(null);
    setSelectedERPItem(null);
    setSelectedERPWarehouse(null);
    setCustomers([]);
    setShipTos([]);
    setItems([]);
    setWarehouse([]);
    onClose();
  };

  // Helper for code-first label
  const codeFirstLabel = (code, name) => {
    if (!code) return name || "";
    if (name && name !== "" && name !== code) return `${code} - ${name}`;
    return code;
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={"md"}
      fullWidth={true}
      PaperProps={{ style: { borderRadius: 15 } }}
    >
      <DialogTitle
        className="add-crossref-dialog-title"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: "var(--primary-main)",
          color: "var(--default-light)",
          fontSize: "24px",
          borderRadius: "15px 15px 0 0",
          padding: "16px 24px",
        }}
      >
        <div style={{ fontWeight: "var(--font-semi-bold)" }}>
          {mode === WorkflowLookupMode.Edit
            ? "Edit Default Warehouse"
            : "Add Default Warehouse"}
        </div>
        <IconButton
          className="closeIconButton"
          style={{ color: "var(--default-light)" }}
          onClick={handleClose}
          size="large"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        className="dialogContent"
        style={{
          backgroundColor: "var(--disabled)",
          padding: "35px",
          minWidth: 400,
        }}
      >
        <Box sx={{ ml: 3 }}>
          <Box display="flex" alignItems="center" mb={1}>
            <Typography variant="body1" sx={labelSx}>
              Customer Connection:
            </Typography>
            <Autocomplete
              options={customerConnections}
              loading={!customersLoaded}
              getOptionLabel={(opt) =>
                typeof opt === "string" ? opt : opt.Name
              }
              value={selectedCustomerConnection}
              onChange={(event, newValue) =>
                setSelectedCustomerConnection(newValue || null)
              }
              disabled={mode === WorkflowLookupMode.Edit}
              renderInput={(params) => (
                <TextField
                  sx={{ minWidth: 350, height: 45 }}
                  {...params}
                  autoComplete="off"
                />
              )}
            />
          </Box>
          <Box display="flex" alignItems="center" mb={1}>
            <Typography variant="body1" sx={labelSx}>
              ERP Connection:
            </Typography>
            <Autocomplete
              options={ERPConnections}
              getOptionLabel={(opt) =>
                typeof opt === "string" ? opt : opt.Name
              }
              value={selectedERPConnection}
              onChange={(event, newValue) => {
                setSelectedERPConnection(newValue || null);
              }}
              disabled={mode === WorkflowLookupMode.Edit}
              renderInput={(params) => (
                <TextField
                  sx={{ minWidth: 350, height: 45 }}
                  {...params}
                  autoComplete="off"
                />
              )}
            />
          </Box>
          <Box display="flex" alignItems="center" mb={1}>
            <Typography variant="body1" sx={labelSx}>
              ERP Related Customer:
            </Typography>
            <Autocomplete
              options={customers}
              getOptionLabel={(opt) =>
                typeof opt === "string"
                  ? opt
                  : codeFirstLabel(
                      opt.CustomerCode,
                      opt.Name || opt.CustomerName
                    )
              }
              value={selectedERPRelatedCustomer}
              onChange={(event, newValue) => {
                setSelectedERPRelatedCustomer(newValue || null);

                if (
                  selectedERPShipTo &&
                  newValue &&
                  selectedERPShipTo?.Customer &&
                  selectedERPShipTo?.Customer?.CustomerCode !==
                    newValue?.CustomerCode
                ) {
                  setSelectedERPShipTo(null);
                }
              }}
              disabled={!selectedERPConnection}
              loading={customersLoading}
              loadingText={"Loading..."}
              renderInput={(params) => (
                <TextField
                  {...params}
                  sx={{ minWidth: 350, height: 45 }}
                  autoComplete="off"
                />
              )}
            />
          </Box>
          <Box display="flex" alignItems="center" mb={1}>
            <Typography variant="body1" sx={labelSx}>
              ERP Ship To Code:
            </Typography>
            <Autocomplete
              options={
                selectedERPRelatedCustomer
                  ? shipTos.filter(
                      (s) =>
                        s?.Customer &&
                        s?.Customer?.CustomerCode ===
                          selectedERPRelatedCustomer?.CustomerCode
                    )
                  : shipTos
              }
              getOptionLabel={(opt) =>
                typeof opt === "string"
                  ? opt
                  : codeFirstLabel(opt.ShipToCode, opt.ShipToName)
              }
              value={selectedERPShipTo}
              onChange={(event, newValue) => {
                setSelectedERPShipTo(newValue || null);
              }}
              loading={shipTosLoading}
              loadingText={"Loading..."}
              disabled={!selectedERPConnection}
              renderInput={(params) => (
                <TextField
                  {...params}
                  sx={{ minWidth: 350, height: 45 }}
                  autoComplete="off"
                />
              )}
            />
          </Box>
          <Box display="flex" alignItems="center" mb={1}>
            <Typography variant="body1" sx={labelSx}>
              ERP Item Code:
            </Typography>
            <Autocomplete
              options={items}
              getOptionLabel={(opt) =>
                typeof opt === "string"
                  ? opt
                  : codeFirstLabel(opt.ItemCode, opt.ItemDesc)
              }
              value={selectedERPItem}
              onChange={(event, newValue) => {
                if (typeof newValue === "string") {
                  setSelectedERPItem(null);
                } else if (newValue) {
                  setSelectedERPItem(newValue);
                } else {
                  setSelectedERPItem(null);
                }
              }}
              loading={itemsLoading}
              loadingText={"Loading..."}
              disabled={!selectedERPConnection}
              renderInput={(params) => (
                <TextField
                  {...params}
                  sx={{ minWidth: 350, height: 45 }}
                  autoComplete="off"
                />
              )}
            />
          </Box>
          <Box display="flex" alignItems="center" mb={1}>
            <Typography variant="body1" sx={labelSx}>
              ERP Warehouse Code:
            </Typography>
            <Autocomplete
              options={warehouse}
              getOptionLabel={(opt) =>
                typeof opt === "string"
                  ? opt
                  : codeFirstLabel(opt.Code, opt.Name)
              }
              value={selectedERPWarehouse}
              onChange={(event, newValue) => {
                setSelectedERPWarehouse(newValue || null);
              }}
              loading={warehouseLoading}
              loadingText={"Loading..."}
              disabled={!selectedERPConnection}
              renderInput={(params) => (
                <TextField
                  {...params}
                  sx={{ minWidth: 350, height: 45 }}
                  autoComplete="off"
                />
              )}
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions style={{ padding: "16px 24px" }}>
        <ShepButton variant="secondary" onClick={handleClose}>
          Cancel
        </ShepButton>
        <ShepButton onClick={handleSubmit}>
          {mode === WorkflowLookupMode.Edit ? "Save" : "Add"}
        </ShepButton>
      </DialogActions>
    </Dialog>
  );
};

export default AddDefaultWarehousePopup;
